import { API_URL } from '../../../../constants/urls.constants';
import type {
  CommonResponse,
  FailedJobResponse,
  FailedJobsParams,
  GetQueryArgsType,
} from '../../../../types/global.types';
import { appServiceApi } from '../../../apis/app-service.api';
import { APP_API_TAGS } from '../../../app.constants';
import { invalidateOnSuccess } from '../../../app.utils';
import type {
  AllEmployeeParams,
  AllEmployeeResponse,
} from '../emp-dashboard/emp-dashboard.type';
import { resolvePayType } from './emp-payroll.type';
import type {
  PayType,
  PayTypeArg,
  GenerateSalaryBody,
  PerSheetDayUpdate,
  PerSheetParams,
  PerSheetResponse,
  FailedAdjustmentListParams,
  FailedAdjustmentListResponse,
  FailedDailyPayListParams,
  FailedDailyPayListResponse,
  PayrollCalendarResponse,
  PayrollCyclesResponse,
  UpdatePayrollCalendarBody,
  UpdateSalaryGradeBody,
  UpdateSalaryStructureBody,
  AllBonusPoliciesResponse,
  BatchSalarySheetParams,
  BatchSalarySheetResponse,
  BonusPoliciesParams,
  DisbursableEmployeesParams,
  DisbursableEmployeesResponse,
  DisburseRecordByBatchResponse,
  EncashmentBatchParams,
  EncashmentBatchResponse,
  EncashmentByBatchResponse,
  FiscalYearsResponse,
  PayrollBatchParams,
  PayrollBatchResponse,
  PayrollByBatchResponse,
  PayrollsByBatchParams,
  SinglePayrollBatchResponse,
  UpdatePayrollComponentBody,
  UpdatePaymentStatusBody,
  EncashmentRecordsParams,
  GenerateEncashmentBody,
  UpdateEncashmentRecordBody,
  DisburseEncashmentBody,
  BonusPolicyBody,
  DisburseBonusBody,
  SalaryGradeResponse,
  SalaryStructureResponse,
  SingleBonusPolicyResponse,
  FailedPayrollListParams,
  FailedPayrollListResponse,
  PerSheetTemplateParams,
} from './emp-payroll.type';

const {
  SALARY_STRUCTURE,
  SALARY_GRADE,
  PAYROLL_BATCH,
  PAYROLL_SALARY_SHEET,
  BONUS_POLICY,
  PAYROLL_EMPLOYEES,
  PAYROLL_CALENDAR,
  PAYROLL_DAILY_PAY,
} = APP_API_TAGS || {};

export const empPayrollApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET: Salary Structure (arg: null | 'MONTHLY' | 'DAILY')
    getSalaryStructure: builder.query<SalaryStructureResponse, PayTypeArg>({
      query: (arg) => ({
        url: API_URL.salaryStructure,
        method: 'GET',
        params: { payBasis: resolvePayType(arg) },
      }),
      providesTags: (_r, _e, arg) => [
        SALARY_STRUCTURE,
        { type: SALARY_STRUCTURE, id: resolvePayType(arg) },
      ],
    }),

    // POST: Salary Structure
    updateSalaryStructure: builder.mutation<any, UpdateSalaryStructureBody>({
      query: (body) => ({
        url: `${API_URL.salaryStructure}/upsert`,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(SALARY_STRUCTURE),
    }),

    // GET: Salary Grade (arg: null | 'MONTHLY' | 'DAILY')
    getSalaryGrade: builder.query<SalaryGradeResponse, PayTypeArg>({
      query: (arg) => ({
        url: API_URL.salaryGrade,
        method: 'GET',
        params: { payBasis: resolvePayType(arg) },
      }),
      providesTags: (_r, _e, arg) => [
        SALARY_GRADE,
        { type: SALARY_GRADE, id: resolvePayType(arg) },
      ],
    }),

    // POST: Salary Grade
    updateSalaryGrade: builder.mutation<any, UpdateSalaryGradeBody>({
      query: (body) => ({
        url: `${API_URL.salaryGrade}/upsert`,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(SALARY_GRADE),
    }),

    // GET: Payroll Calendar (monthly + daily cycle config)
    getPayrollCalendar: builder.query<PayrollCalendarResponse, PayTypeArg>({
      query: (arg) => ({
        url: API_URL.payrollCalendar,
        method: 'GET',
        params: { payBasis: resolvePayType(arg) },
      }),
      providesTags: (_r, _e, arg) => [
        PAYROLL_CALENDAR,
        { type: PAYROLL_CALENDAR, id: resolvePayType(arg) },
      ],
    }),

    // POST: one population's cycle
    updatePayrollCalendar: builder.mutation<any, UpdatePayrollCalendarBody>({
      query: (body) => ({
        url: API_URL.payrollCalendar,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PAYROLL_CALENDAR, PAYROLL_BATCH]),
    }),

    // GET: the cycles a month splits into, straight from the server
    getPayrollCycles: builder.query<
      PayrollCyclesResponse,
      { payBasis: PayType; year: number | string; month: string }
    >({
      query: (params) => ({
        url: `${API_URL.payrollCalendar}/cycles`,
        method: 'GET',
        params,
      }),
      providesTags: [PAYROLL_CALENDAR],
    }),

    // ---------------------------------------------------------------
    // Daily Pay Sheet — the per-sheet view of the payroll calendar (JTI)
    // ---------------------------------------------------------------

    getPerSheet: builder.query<PerSheetResponse, PerSheetParams>({
      query: (params) => ({
        url: `${API_URL.payrollCalendar}/per-sheet`,
        method: 'GET',
        params,
      }),
      providesTags: [PAYROLL_DAILY_PAY],
    }),

    /** Everything the admin changed since entering edit mode, in one call. */
    updatePerSheetBulk: builder.mutation<any, { updates: PerSheetDayUpdate[] }>(
      {
        query: (body) => ({
          url: `${API_URL.payrollCalendar}/per-sheet/bulk`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: invalidateOnSuccess([
          PAYROLL_DAILY_PAY,
          PAYROLL_BATCH,
        ]),
      },
    ),

    // GET: pre-filled sheet template (daily employees). MONTHLY carries every
    // date of the month, or of one cycle with `cycleNo`; DAILY a single `date`.
    downloadPerSheetTemplate: builder.query<string, PerSheetTemplateParams>({
      query: (params) => ({
        url: `${API_URL.payrollCalendar}/template`,
        params,
        cache: 'no-cache',
        responseHandler: async (response: Response) => {
          const blob = await response.blob();
          return window.URL.createObjectURL(blob);
        },
      }),
      keepUnusedDataFor: 0,
    }),

    // POST: upload a filled sheet (queued server side)
    uploadPerSheet: builder.mutation<
      any,
      { file: File; templateType: 'DAILY' | 'MONTHLY' }
    >({
      query: ({ file, templateType }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `${API_URL.payrollCalendar}/upload`,
          method: 'POST',
          params: { templateType },
          body: formData,
        };
      },
      invalidatesTags: invalidateOnSuccess([PAYROLL_DAILY_PAY, PAYROLL_BATCH]),
    }),

    getFailedPerSheetJobs: builder.query<
      CommonResponse<FailedJobResponse>,
      GetQueryArgsType<FailedJobsParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.payrollCalendar}/upload/failed/jobs`,
        method: 'GET',
        ...queryArgs,
      }),
    }),

    getFailedPerSheetRecords: builder.query<
      CommonResponse<FailedDailyPayListResponse>,
      GetQueryArgsType<FailedDailyPayListParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.payrollCalendar}/upload/failed/list`,
        method: 'GET',
        ...queryArgs,
      }),
    }),

    // ---------------------------------------------------------------
    // Addition / deduction / tax bulk upload inside a batch (JTI)
    // ---------------------------------------------------------------

    // GET: template pre-filled with the batch's employees
    downloadAdjustmentTemplate: builder.query<string, { batch_id: string }>({
      query: ({ batch_id }) => ({
        url: `${API_URL.payrollAdjustments}/template`,
        params: { batchId: batch_id },
        cache: 'no-cache',
        responseHandler: async (response: Response) =>
          window.URL.createObjectURL(await response.blob()),
      }),
    }),

    // POST: upload the filled sheet
    uploadAdjustments: builder.mutation<any, { file: File; batch_id: string }>({
      query: ({ file, batch_id }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `${API_URL.payrollAdjustments}/upload`,
          method: 'POST',
          params: { batchId: batch_id },
          body: formData,
        };
      },
      invalidatesTags: invalidateOnSuccess(PAYROLL_BATCH),
    }),

    // GET: upload attempts that had rejected rows
    getFailedAdjustmentJobs: builder.query<
      CommonResponse<FailedJobResponse>,
      GetQueryArgsType<FailedJobsParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.payrollAdjustments}/upload/failed/jobs`,
        method: 'GET',
        ...queryArgs,
      }),
    }),

    // GET: failed rows of one attempt
    getFailedAdjustmentRecords: builder.query<
      CommonResponse<FailedAdjustmentListResponse>,
      GetQueryArgsType<FailedAdjustmentListParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.payrollAdjustments}/upload/failed/list`,
        method: 'GET',
        ...queryArgs,
      }),
    }),

    // GET: All Payroll Batch
    getAllPayrollBatch: builder.query<
      PayrollBatchResponse,
      GetQueryArgsType<PayrollBatchParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.payrollBatch,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [PAYROLL_BATCH],
    }),

    // GET: All Payrolls By Batch (by BatchID)
    getAllPayrollsByBatch: builder.query<
      PayrollByBatchResponse,
      { id: string; args: GetQueryArgsType<PayrollsByBatchParams> }
    >({
      query: ({ id, args }) => ({
        url: `${API_URL.payrollBatch}/${id}/salary-sheet`,
        method: 'GET',
        ...args,
      }),
      providesTags: (_, __, { id }) => [{ type: PAYROLL_BATCH, id }],
    }),

    // GET: one batch with every employee row (`history`), unpaginated
    getSinglePayrollBatch: builder.query<SinglePayrollBatchResponse, string>({
      query: (id) => ({ url: `${API_URL.payrollBatch}/${id}`, method: 'GET' }),
      providesTags: (_, __, id) => [{ type: PAYROLL_BATCH, id }],
    }),

    // DELETE: Payroll Batch
    deletePayrollBatch: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `${API_URL.payrollBatch}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess(PAYROLL_BATCH),
    }),

    // GET: Batch Salary Sheet (by ID)
    getBatchSalarySheet: builder.query<
      BatchSalarySheetResponse,
      { id: string; args: GetQueryArgsType<BatchSalarySheetParams> }
    >({
      query: ({ id, args }) => ({
        url: `${API_URL.payrollBatch}/${id}/bank-list`,
        method: 'GET',
        ...args,
      }),
      providesTags: [PAYROLL_SALARY_SHEET],
    }),

    getPayrollAbleEmployees: builder.query<
      CommonResponse<AllEmployeeResponse>,
      GetQueryArgsType<AllEmployeeParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.employees,
        ...queryArgs,
      }),
      providesTags: [PAYROLL_EMPLOYEES],
    }),

    // POST: Generate Salary
    generateSalary: builder.mutation<any, GenerateSalaryBody>({
      query: (body) => ({
        url: API_URL.generateSalary,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PAYROLL_BATCH, PAYROLL_EMPLOYEES]),
    }),

    // POST: one row's addition / deduction / tax / note
    updatePayrollComponent: builder.mutation<any, UpdatePayrollComponentBody>({
      query: (body) => ({
        url: API_URL.updateBonusDeduction,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(PAYROLL_BATCH),
    }),

    // POST: mark rows' salary sheets as generated (amounts become final)
    generateSalarySheet: builder.mutation<any, { ids: string[] }>({
      query: (body) => ({
        url: API_URL.generateSalarySheet,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PAYROLL_BATCH]),
    }),

    // POST: mark every salary sheet of a batch as generated
    generateBatchSheets: builder.mutation<any, { batchId: string }>({
      query: ({ batchId }) => ({
        url: `${API_URL.payrollBatch}/${batchId}/generate-sheets`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess([PAYROLL_BATCH]),
    }),

    // POST: set the payment status of every record in a batch
    updateBatchPaymentStatus: builder.mutation<
      any,
      { batchId: string; status: 'PAID' | 'UNPAID' }
    >({
      query: ({ batchId, status }) => ({
        url: `${API_URL.payrollBatch}/${batchId}/payment-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: invalidateOnSuccess([
        PAYROLL_BATCH,
        PAYROLL_SALARY_SHEET,
      ]),
    }),

    // POST: mark bank-list rows as paid
    updatePaymentStatus: builder.mutation<any, UpdatePaymentStatusBody>({
      query: (body) => ({
        url: API_URL.updatePaymentStatus,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([
        PAYROLL_BATCH,
        PAYROLL_SALARY_SHEET,
      ]),
    }),

    // POST: Generate Leave Encashment
    generateLeaveEncashment: builder.mutation<any, GenerateEncashmentBody>({
      query: (body) => ({
        url: API_URL.generateLeaveEncashment,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(PAYROLL_BATCH),
    }),

    // GET: All Encashment Batch
    getAllEncashmentBatch: builder.query<
      EncashmentBatchResponse,
      GetQueryArgsType<EncashmentBatchParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.encashmentBatch}`,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [PAYROLL_BATCH],
    }),

    // DELETE: Encashment Batch
    deleteEncashmentBatch: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `${API_URL.encashmentBatch}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess(PAYROLL_BATCH),
    }),

    // GET: All Encashments By Batch
    getAllEncashmentByBatch: builder.query<
      EncashmentByBatchResponse,
      {
        id: string;
        args: GetQueryArgsType<EncashmentRecordsParams>;
      }
    >({
      query: ({ id, args }) => ({
        url: `${API_URL.encashmentBatch}/${id}`,
        method: 'GET',
        ...args,
      }),
      providesTags: [PAYROLL_BATCH],
    }),

    // GET: Disburse Record By Batch
    getDisburseRecordByBatch: builder.query<
      DisburseRecordByBatchResponse,
      {
        id: string;
        args: GetQueryArgsType<EncashmentRecordsParams>;
      }
    >({
      query: ({ id, args }) => ({
        url: `${API_URL.encashmentBatch}/${id}/disbursed`,
        method: 'GET',
        ...args,
      }),
      providesTags: [PAYROLL_BATCH],
    }),

    // POST: Recalculate Batch
    recalculateBatch: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `${API_URL.encashmentBatch}/${id}/recalculate`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess(PAYROLL_BATCH),
    }),

    // PATCH: one record's breakdown amounts and note (record must be PENDING)
    updateEncashmentRecord: builder.mutation<
      any,
      { batchId: string; recordId: string; body: UpdateEncashmentRecordBody }
    >({
      query: ({ batchId, recordId, body }) => ({
        url: `${API_URL.encashmentBatch}/${batchId}/records/${recordId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(PAYROLL_BATCH),
    }),

    // POST: move encashment amounts into a payroll month
    disburseEncashment: builder.mutation<any, DisburseEncashmentBody>({
      query: (body) => ({
        url: API_URL.disburseEncashment,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(PAYROLL_BATCH),
    }),

    // GET: Fiscal Years
    getFiscalYears: builder.query<
      FiscalYearsResponse,
      { previous_year?: number }
    >({
      query: ({ previous_year }) => ({
        url: API_URL.fiscalYears,
        method: 'GET',
        params: previous_year ? { previous_year } : undefined,
      }),
    }),

    // GET: Disbursable Employees
    getDisbursableEmployees: builder.query<
      DisbursableEmployeesResponse,
      { id: string; args: GetQueryArgsType<DisbursableEmployeesParams> }
    >({
      query: ({ id, args }) => ({
        url: `${API_URL.disbursableEmployees}/${id}/employees`,
        method: 'GET',
        ...args,
      }),
    }),

    // GET: All Bonus Policies
    getAllBonusPolicies: builder.query<
      AllBonusPoliciesResponse,
      GetQueryArgsType<BonusPoliciesParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.bonusPolicy}`,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [BONUS_POLICY],
    }),

    // GET: Single Bonus Policy
    getSingleBonusPolicy: builder.query<
      SingleBonusPolicyResponse,
      { id: string; bonus_month?: string; bonus_year?: number }
    >({
      query: ({ id, bonus_month, bonus_year }) => ({
        url: `${API_URL.bonusPolicy}/${id}`,
        method: 'GET',
        params: {
          ...(bonus_month ? { bonus_month } : {}),
          ...(bonus_year ? { bonus_year } : {}),
        },
      }),
      providesTags: (_result, _error, { id }) => [
        { type: BONUS_POLICY as any, id },
      ],
    }),

    // POST: Create New Bonus Policy
    createNewBonusPolicy: builder.mutation<any, BonusPolicyBody>({
      query: (body) => ({
        url: `${API_URL.bonusPolicy}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(BONUS_POLICY),
    }),

    // PATCH: Update Bonus Policy
    updateBonusPolicy: builder.mutation<
      any,
      { id: string; body: Partial<BonusPolicyBody> }
    >({
      query: ({ id, body }) => ({
        url: `${API_URL.bonusPolicy}/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([
        BONUS_POLICY,
        {
          type: BONUS_POLICY,
          id: true,
        },
      ]),
    }),


    // POST: pay a policy's bonus with a salary month
    disburseBonus: builder.mutation<any, DisburseBonusBody>({
      query: (body) => ({
        url: `${API_URL.disburseBonus}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(PAYROLL_BATCH),
    }),
    // POST: queue the bank list report for a batch (downloaded from Reports)
    generateBankListReport: builder.mutation<any, { batchId: string }>({
      query: (body) => ({
        url: API_URL.generateBankListReport,
        method: 'POST',
        body,
      }),
    }),
    // ---------------------------------------------------------------
    // Payroll bulk import (Excel)
    // ---------------------------------------------------------------
    downloadPayrollTemplate: builder.query<string, void>({
      query: () => ({
        url: `${API_URL.payrollBulkImport}/template`,
        responseHandler: async (response: Response) => {
          const blob = await response.blob();
          return window.URL.createObjectURL(blob);
        },
        cache: 'no-cache',
      }),
    }),
    uploadPayroll: builder.mutation<CommonResponse<any>, FormData>({
      query: (body) => ({
        url: `${API_URL.generateSalary}/bulk-import`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (res) => (res ? [PAYROLL_BATCH] : []),
    }),
    getFailedPayrollJobs: builder.query<
      CommonResponse<FailedJobResponse>,
      GetQueryArgsType<FailedJobsParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.payrollBulkImport}/failed/jobs`,
        ...queryArgs,
      }),
      providesTags: [PAYROLL_BATCH],
    }),
    getFailedPayrollRecords: builder.query<
      CommonResponse<FailedPayrollListResponse>,
      GetQueryArgsType<FailedPayrollListParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.payrollBulkImport}/failed/list`,
        ...queryArgs,
      }),
      providesTags: [PAYROLL_BATCH],
    }),
  }),
});

export const {
  useGetSalaryStructureQuery,
  useGetSalaryGradeQuery,
  useUpdateSalaryStructureMutation,
  useUpdateSalaryGradeMutation,
  useGetPayrollCalendarQuery,
  useUpdatePayrollCalendarMutation,
  useGetPayrollCyclesQuery,
  // Daily Pay Sheet
  useGetPerSheetQuery,
  useUpdatePerSheetBulkMutation,
  useLazyDownloadAdjustmentTemplateQuery,
  useUploadAdjustmentsMutation,
  useGetFailedAdjustmentJobsQuery,
  useGetFailedAdjustmentRecordsQuery,
  useUploadPerSheetMutation,
  useLazyDownloadPerSheetTemplateQuery,
  useGetFailedPerSheetJobsQuery,
  useGetFailedPerSheetRecordsQuery,
  useGetAllPayrollBatchQuery,
  useGetAllPayrollsByBatchQuery,
  useGetSinglePayrollBatchQuery,
  useGetBatchSalarySheetQuery,
  useGenerateSalaryMutation,
  useGetPayrollAbleEmployeesQuery,
  useUpdatePayrollComponentMutation,
  useGenerateSalarySheetMutation,
  useUpdatePaymentStatusMutation,
  useGenerateBatchSheetsMutation,
  useUpdateBatchPaymentStatusMutation,
  useDeletePayrollBatchMutation,
  useGenerateLeaveEncashmentMutation,
  useGetAllEncashmentBatchQuery,
  useDeleteEncashmentBatchMutation,
  useGetAllEncashmentByBatchQuery,
  useGetDisburseRecordByBatchQuery,
  useRecalculateBatchMutation,
  useUpdateEncashmentRecordMutation,
  useDisburseEncashmentMutation,
  useGetFiscalYearsQuery,
  useGetDisbursableEmployeesQuery,
  useGetAllBonusPoliciesQuery,
  useGetSingleBonusPolicyQuery,
  useCreateNewBonusPolicyMutation,
  useUpdateBonusPolicyMutation,
  useDisburseBonusMutation,
  useGenerateBankListReportMutation,

  // Payroll Bulk Import
  useLazyDownloadPayrollTemplateQuery,
  useUploadPayrollMutation,
  useGetFailedPayrollJobsQuery,
  useGetFailedPayrollRecordsQuery,
} = empPayrollApi;
